import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import type { CreateGroupBody } from '../api/groups.js';

const GROUP_NAME_RE = /^[a-z][a-z0-9-]{0,63}$/;

interface GroupCreateFormProps {
  readonly submitting: boolean;
  readonly error: string | null;
  readonly onSubmit: (body: CreateGroupBody) => void;
}

export function GroupCreateForm({
  submitting,
  error,
  onSubmit,
}: GroupCreateFormProps): JSX.Element {
  const [name, setName] = useState('');
  const [membersRaw, setMembersRaw] = useState('');

  const nameError =
    name.trim().length > 0 && !GROUP_NAME_RE.test(name.trim())
      ? 'Must start with a lowercase letter; only lowercase letters, digits, and hyphens allowed (max 64 chars).'
      : null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const members = membersRaw
          .split(',')
          .map((m) => m.trim())
          .filter((m) => m.length > 0);
        onSubmit({ name: name.trim(), members });
      }}
      className="grid gap-3 rounded-2xl border border-border-subtle bg-surface/80 p-5 backdrop-blur"
    >
      <Field label="Group name">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="my-group"
          required
          aria-label="Group name"
          aria-invalid={nameError !== null}
          className={`w-full rounded-lg border bg-deep px-3 py-2 text-sm text-text-primary outline-none transition-all focus:ring-2 ${
            nameError
              ? 'border-rose-500/60 focus:border-rose-500/60 focus:ring-rose-500/20'
              : 'border-border-subtle focus:border-accent/40 focus:ring-accent/20'
          }`}
        />
        {nameError && <p className="text-[10px] text-rose-300">{nameError}</p>}
      </Field>
      <Field label="Members (comma-separated repo slugs)">
        <input
          type="text"
          value={membersRaw}
          onChange={(e) => setMembersRaw(e.target.value)}
          placeholder="repo-a, repo-b"
          required
          aria-label="Group members"
          className="w-full rounded-lg border border-border-subtle bg-deep px-3 py-2 font-mono text-sm text-text-primary outline-none transition-all focus:border-accent/40 focus:ring-2 focus:ring-accent/20"
        />
      </Field>
      {error && (
        <p
          role="alert"
          className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300"
        >
          {error}
        </p>
      )}
      <div>
        <button
          type="submit"
          disabled={submitting || nameError !== null}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-accent to-accent-dim px-4 py-2 text-sm font-semibold text-white shadow-glow transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none"
        >
          {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {submitting ? 'Creating…' : 'Create group'}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-[10px] font-mono uppercase tracking-[0.2em] text-text-muted">
      {label}
      {children}
    </label>
  );
}
